import { useMemo, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Flask, Sparkle, UploadSimple } from "@phosphor-icons/react";
import HseQuestionField from "./HseQuestionField";

/** Add a product to the COSHH register: the assessment questions (shared with the HSE catalogue) + which documents the AI
 *  should write now, with an optional manufacturer SDS upload that the data sheet is summarised from. */
export default function CoshhNewDialog({ open, onOpenChange, questions, onCreated }) {
  const [answers, setAnswers] = useState({});
  const [storage, setStorage] = useState("");
  const [gen, setGen] = useState({ assessment: true, data_sheet: true });
  const [sds, setSds] = useState(null);
  const [busy, setBusy] = useState(false);
  const qs = useMemo(() => questions || [], [questions]);
  const set = (k, v) => setAnswers(a => ({ ...a, [k]: v }));

  const submit = async () => {
    if (!answers.product || !answers.use) { toast.error("Give the product name and what it is used for"); return; }
    setBusy(true);
    try {
      const generate = Object.keys(gen).filter(k => gen[k]);
      const { data } = await API.post("/coshh/substances", { ...answers, storage_location: storage, generate: sds ? generate.filter(g => g !== "data_sheet") : generate });
      if (sds) {
        const fd = new FormData();
        fd.append("file", sds);
        try { await API.post(`/coshh/substances/${data.id}/sds`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (e) { toast.error(`SDS upload: ${formatApiError(e)} — the data sheet can be written from the product details instead`); }
      }
      toast.success(`${data.reference} added — the AI is writing the documents now`);
      setAnswers({}); setStorage(""); setSds(null);
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="coshh-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flask size={18} /> Add a substance to the COSHH register</DialogTitle>
          <DialogDescription>Answer what you know about the product. The AI writes a full COSHH assessment and a 16-section product data sheet from it — or summarises the manufacturer's SDS if you upload one.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {qs.map(q => (
            <div key={q.key} className={q.type === "textarea" || q.type === "multiselect" ? "sm:col-span-2" : ""}>
              <HseQuestionField q={q} value={answers[q.key]} onChange={v => set(q.key, v)} testPrefix="coshh-q" />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label className="text-xs">Where it is stored</Label>
            <Input value={storage} onChange={e => setStorage(e.target.value)} placeholder="e.g. van COSHH cabinet, unit 4 store" data-testid="coshh-q-storage" />
          </div>
        </div>
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1"><Sparkle size={12} /> Write with AI now</p>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={gen.assessment} onChange={e => setGen(g => ({ ...g, assessment: e.target.checked }))} data-testid="coshh-gen-assessment" /> COSHH assessment (14 sections, risk evaluation table)</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={gen.data_sheet} onChange={e => setGen(g => ({ ...g, data_sheet: e.target.checked }))} data-testid="coshh-gen-data-sheet" /> Product data sheet (16 SDS sections, hazard statements, PPE)</label>
          <div className="flex items-center gap-2 pt-1">
            <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border bg-background cursor-pointer hover:bg-muted">
              <UploadSimple size={14} /> {sds ? sds.name : "Upload the manufacturer's SDS (PDF) — optional"}
              <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={e => setSds(e.target.files?.[0] || null)} data-testid="coshh-sds-file" />
            </label>
            {sds && <button type="button" className="text-xs text-muted-foreground underline" onClick={() => setSds(null)}>remove</button>}
          </div>
          <p className="text-[11px] text-muted-foreground">With an SDS uploaded the data sheet is a faithful summary of it; without one the AI writes it from the details above and flags anything to verify.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="coshh-new-submit"><Sparkle size={14} className="mr-1" /> {busy ? "Adding…" : "Add & write documents"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
